/**
 * Manages keyboard + mouse input.
 */
export class InputManager {
  constructor() {
    this.keys = new Set();
    this.mouseButtons = new Set();
    this.mouseDX = 0;
    this.mouseDY = 0;
    this.isPointerLocked = false;
    this._justPressed = new Set();
    this._callbacks = new Map();
    this.isMobileDevice = typeof window !== 'undefined' && (
      'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0
    );
    this._touchLook = { active: false, pointerId: null, prevX: 0, prevY: 0 };
    this._touchLookVector = { x: 0, y: 0 };
    this.enabled = true;

    this._bindListeners();
  }

  _bindListeners() {
    document.addEventListener('keydown', (e) => {
      if (!this.enabled || e.repeat) return;
      // Prevent Tab from changing focus (used for journal)
      if (e.code === 'Tab') e.preventDefault();
      this.keys.add(e.code);
      this._justPressed.add(e.code);
      this._fire('keydown', e.code);
    });

    document.addEventListener('keyup', (e) => {
      if (!this.enabled) return;
      this.keys.delete(e.code);
      this._fire('keyup', e.code);
    });

    document.addEventListener('mousedown', (e) => {
      if (!this.enabled) return;
      this.mouseButtons.add(e.button);
    });

    document.addEventListener('mouseup', (e) => {
      if (!this.enabled) return;
      this.mouseButtons.delete(e.button);
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.enabled) return;
      if (this.isPointerLocked) {
        this.mouseDX += e.movementX;
        this.mouseDY += e.movementY;
      }
    });

    document.addEventListener('pointerlockchange', () => {
      this.isPointerLocked = !!document.pointerLockElement;
    });
  }

  isMobile() {
    return this.isMobileDevice;
  }

  bindMobileLook(element) {
    if (!this.isMobileDevice || !element) return;

    const onTouchStart = (e) => {
      if (this._touchLook.active) return;
      const touch = e.changedTouches[0];
      if (!touch) return;
      this._touchLook.active = true;
      this._touchLook.pointerId = touch.identifier;
      this._touchLook.prevX = touch.clientX;
      this._touchLook.prevY = touch.clientY;
      e.preventDefault();
    };

    const onTouchMove = (e) => {
      if (!this._touchLook.active) return;
      for (const touch of e.changedTouches) {
        if (touch.identifier !== this._touchLook.pointerId) continue;
        const dx = touch.clientX - this._touchLook.prevX;
        const dy = touch.clientY - this._touchLook.prevY;
        this.mouseDX += dx;
        this.mouseDY += dy;
        this._touchLook.prevX = touch.clientX;
        this._touchLook.prevY = touch.clientY;
        e.preventDefault();
        break;
      }
    };

    const onTouchEnd = (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier !== this._touchLook.pointerId) continue;
        this._touchLook.active = false;
        this._touchLook.pointerId = null;
        break;
      }
    };

    element.addEventListener('touchstart', onTouchStart, { passive: false });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: false });
    element.addEventListener('touchcancel', onTouchEnd, { passive: false });
  }

  bindVirtualButton(code, element) {
    if (!element) return;

    const fireDown = () => {
      // Don't preventDefault here so touchmove can still bubble up to zones if needed
      // or at least not block the OS-level touch handling unnecessarily
      if (!this.keys.has(code)) {
        this.keys.add(code);
        this._justPressed.add(code);
      }
    };

    const fireUp = () => {
      this.keys.delete(code);
    };

    element.addEventListener('touchstart', fireDown, { passive: true });
    element.addEventListener('mousedown', fireDown);
    element.addEventListener('touchend', fireUp, { passive: true });
    element.addEventListener('touchcancel', fireUp, { passive: true });
    element.addEventListener('mouseup', fireUp);
    element.addEventListener('mouseleave', fireUp);
  }

  /**
   * Returns true only once per key press (not held).
   */
  justPressed(code) {
    if (this._justPressed.has(code)) {
      this._justPressed.delete(code);
      return true;
    }
    return false;
  }

  consumeMouse() {
    const dx = this.mouseDX + this._touchLookVector.x;
    const dy = this.mouseDY + this._touchLookVector.y;
    this.mouseDX = 0;
    this.mouseDY = 0;
    return { dx, dy };
  }

  setTouchLookVector(x, y) {
    this._touchLookVector.x = x;
    this._touchLookVector.y = y;
  }

  clearTouchLookVector() {
    this._touchLookVector.x = 0;
    this._touchLookVector.y = 0;
  }

  requestPointerLock(element) {
    // Pointer Lock is not needed on touch devices and can throw on iOS Safari
    if (this.isMobileDevice) return;
    try {
      const result = element.requestPointerLock();
      // Modern browsers return a Promise — catch rejections silently
      if (result && result.catch) result.catch(() => {});
    } catch (_) {
      // iOS Safari may throw DOMException; ignore
    }
  }

  exitPointerLock() {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }

  setEnabled(enabled) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;

    if (!enabled) {
      this.keys.clear();
      this.mouseButtons.clear();
      this._justPressed.clear();
      this.mouseDX = 0;
      this.mouseDY = 0;
      this._touchLook.active = false;
      this._touchLook.pointerId = null;
      this._touchLookVector.x = 0;
      this._touchLookVector.y = 0;
    }
  }

  on(event, callback) {
    if (!this._callbacks.has(event)) this._callbacks.set(event, new Set());
    this._callbacks.get(event).add(callback);
  }

  _fire(event, data) {
    if (this._callbacks.has(event)) {
      for (const cb of this._callbacks.get(event)) cb(data);
    }
  }

  resetFrame() {
    this._justPressed.clear();
  }
}
